import { gql } from '@apollo/client';
import { Query } from 'core/query';
import { Mutation } from 'core/mutation';
import { Subscription } from 'core/subscription';

const CREATOR_QUERY = gql`
  query AuthorQuery($userId: ID) {
    userById(id: $userId) {
      id
      profile {
        id
        username
        fullName
        user {
          id
        }
        avatar {
          id
          filename
          path
          url
          completed
        }
        imageFallbackChar
      }
    }
  }
`;

export class CreatorQuery extends Query {
  static conf = {
    GQL: CREATOR_QUERY,
    resultPath: 'userById.profile',
  };
}

const VECTOR_FRAGMENT = gql`
  fragment vector on Vector {
    x
    y
    z
  }
`;

const MESSAGE_FRAGMENT = gql`
  fragment activityMessage on Message {
    id
    messageType
    timestamp
    createdBy {
      id
      profile {
        id
        username
        fullName
        user {
          id
        }
        avatar {
          id
          filename
          path
          url
          completed
        }
        imageFallbackChar
      }
    }
    contextId
    referenceId
    resourceId
    body
    tempKey
  }
`;

const TARGET_FRAGMENT = gql`
  ${VECTOR_FRAGMENT}
  fragment annotationTarget on Target {
    targetType
    selType
    instanceIx
    brepIx
    bodyIx
    prcPath
    faceHit
    faceIx
    loopIx
    edgeIx
    pointHit
    pointIx
    triangleIx
    trianglePt {
      ...vector
    }
    triangleNr {
      ...vector
    }
  }
`;

const CAMERA_FRAGMENT = gql`
  ${VECTOR_FRAGMENT}
  fragment annotationCamera on Camera {
    up {
      ...vector
    }
    right {
      ...vector
    }
    position {
      ...vector
    }
    lookat {
      ...vector
    }
  }
`;

const VIEWER_STATE_FRAGMENT = gql`
  fragment annotationViewerState on ViewerState {
    blob
  }
`;

const ANNOTATION_FRAGMENT = gql`
  ${TARGET_FRAGMENT}
  ${CAMERA_FRAGMENT}
  ${VIEWER_STATE_FRAGMENT}
  fragment annotation on Annotation {
    id
    contextId
    resourceId
    body
    createdBy
    target {
      ...annotationTarget
    }
    timestamp
    camera {
      ...annotationCamera
    }
    tempKey
    viewerState {
      ...annotationViewerState
    }
  }
`;

const PATCH_FRAGMENT = gql`
  ${ANNOTATION_FRAGMENT}
  fragment patch on Patch {
    type
    action
    data {
      ...annotation
    }
  }
`;
// Messages

const MESSAGES_QUERY = gql`
  ${MESSAGE_FRAGMENT}
  query MessagesQuery($first: Int, $getMessagesInput: GetMessagesInput) {
    messages(first: $first, getMessagesInput: $getMessagesInput) {
      nodes {
        ...activityMessage
      }
      # pageInfo {
      #   endCursor
      #   hasNextPage
      # }
    }
  }
`;

export class ContextMessagesQuery extends Query {
  static conf = {
    GQL: MESSAGES_QUERY,
    resultPath: 'messages',
  };
}

const CONVERSATION_QUERY = gql`
  ${MESSAGE_FRAGMENT}
  query ConversationQuery(
    $contextId: String!
    $resourceId: String
    $referenceId: String
    $first: Int
    $offset: Int
  ) {
    conversationByContext(
      contextId: $contextId
      resourceId: $resourceId
      referenceId: $referenceId
    ) {
      id
      type
      contextId
      resourceId
      unreadCount
      space {
        id
      }
      lastMessage {
        ...activityMessage
      }
      lastReadMessage {
        id
      }
      messages(first: $first, offset: $offset) {
        total
        nodes {
          id
          messageType
          timestamp
          createdBy {
            id
            profile {
              id
              username
              fullName
              user {
                id
              }
              avatar {
                id
                filename
                path
                url
                completed
              }
              imageFallbackChar
            }
          }
          contextId
          referenceId
          resourceId
          body
          tempKey
        }
      }
    }
  }
`;

export class ContextConversationQuery extends Query {
  static conf = {
    GQL: CONVERSATION_QUERY,
    resultPath: 'conversationByContext',
  };
}

// TODO: use activityMessage fragment after fixing issue with federation
const INTERMEDIATOR_CONVERSATION_QUERY = gql`
  ${MESSAGE_FRAGMENT}
  query IntermediatorConversationQuery(
    $contextId: String!
    $resourceId: String
    $referenceId: String
    $first: Int
    $offset: Int
  ) {
    conversationByContext(
      contextId: $contextId
      resourceId: $resourceId
      referenceId: $referenceId
    ) {
      id
      type
      contextId
      resourceId
      unreadCount
      space {
        id
      }
      lastMessage {
        ...activityMessage
      }
      lastReadMessage {
        id
      }
      participants {
        id
        user {
          id
          username
          profile {
            id
            fullName
            avatar {
              id
              url
            }
          }
        }
      }
      messages(first: $first, offset: $offset) {
        total
        nodes {
          id
          messageType
          timestamp
          updatedAt
          createdBy {
            id
            profile {
              id
              username
              fullName
              user {
                id
              }
              avatar {
                id
                filename
                path
                url
                completed
              }
              imageFallbackChar
            }
          }
          contextId
          referenceId
          resourceId
          body
          tempKey
          readByCustomer
          sentAs {
            id
            username
          }
        }
      }
    }
  }
`;

export class IntermediatorConversationQuery extends Query {
  static conf = {
    GQL: INTERMEDIATOR_CONVERSATION_QUERY,
    resultPath: 'conversationByContext',
  };
}

const CREATE_MESSAGE_MUTATION = gql`
  ${MESSAGE_FRAGMENT}
  mutation CreateMessage($createMessageInput: CreateMessageInput) {
    createMessage(createMessageInput: $createMessageInput) {
      ...activityMessage
    }
  }
`;

export class CreateMessageMutation extends Mutation {
  static conf = {
    GQL: CREATE_MESSAGE_MUTATION,
    inputName: 'createMessageInput',
    resultPath: 'createMessage',
  };
}

const UPDATE_MESSAGE_MUTATION = gql`
  mutation UpdateMessage($updateMessageInput: UpdateMessageInput) {
    updateMessage(updateMessageInput: $updateMessageInput) {
      id
      body
    }
  }
`;

export class UpdateMessageMutation extends Mutation {
  static conf = {
    GQL: UPDATE_MESSAGE_MUTATION,
    inputName: 'updateMessageInput',
    resultPath: 'updateMessage',
  };
}

const DELETE_MESSAGE_MUTATION = gql`
  ${MESSAGE_FRAGMENT}
  mutation DeleteMessage($deleteMessageInput: DeleteMessageInput) {
    deleteMessage(deleteMessageInput: $deleteMessageInput) {
      ...activityMessage
    }
  }
`;

export class DeleteMessageMutation extends Mutation {
  static conf = {
    GQL: DELETE_MESSAGE_MUTATION,
    inputName: 'deleteMessageInput',
    resultPath: 'deleteMessage',
  };
}

const READ_MESSAGE_MUTATION = gql`
  ${MESSAGE_FRAGMENT}
  mutation ReadMessage($readMessageInput: ReadMessageInput) {
    readMessage(readMessageInput: $readMessageInput) {
      ...activityMessage
    }
  }
`;

export class ReadMessageMutation extends Mutation {
  static conf = {
    GQL: READ_MESSAGE_MUTATION,
    inputName: 'readMessageInput',
    resultPath: 'readMessage',
  };
}

// For the creation and deletion subscription, we cannot use the
// activityMessage as is, cause the federation is not resolving the
// createdBy. For both, createdBy will be a string
const MESSAGE_CREATED_SUBSCRIPTION = gql`
  subscription MessageCreatedSubscription(
    $subscribeMessageInput: SubscribeMessageInput
  ) {
    messageCreated(subscribeMessageInput: $subscribeMessageInput) {
      id
      messageType
      timestamp
      createdBy
      contextId
      referenceId
      resourceId
      body
      tempKey
    }
  }
`;

export class MessageCreatedSubscription extends Subscription {
  static conf = {
    GQL: MESSAGE_CREATED_SUBSCRIPTION,
    resultPath: 'messageCreated',
  };
}

const MESSAGE_DELETED_SUBSCRIPTION = gql`
  subscription MessageDeletedSubscription(
    $subscribeMessageInput: SubscribeMessageInput
  ) {
    messageDeleted(subscribeMessageInput: $subscribeMessageInput) {
      id
      messageType
      timestamp
      createdBy
      contextId
      referenceId
      resourceId
      body
      tempKey
    }
  }
`;

export class MessageDeletedSubscription extends Subscription {
  static conf = {
    GQL: MESSAGE_DELETED_SUBSCRIPTION,
    resultPath: 'messageDeleted',
  };
}

// Annotations

const ANNOTATIONS_QUERY = gql`
  ${ANNOTATION_FRAGMENT}
  query AnnotationsQuery($getAnnotationsInput: GetAnnotationsInput) {
    annotations(getAnnotationsInput: $getAnnotationsInput) {
      ...annotation
    }
  }
`;

export class AnnotationsQuery extends Query {
  static conf = {
    GQL: ANNOTATIONS_QUERY,
    resultPath: '',
  };
}

const CREATE_ANNOTATION_MUTATION = gql`
  ${ANNOTATION_FRAGMENT}
  mutation CreateAnnotation($createAnnotationInput: CreateAnnotationInput) {
    createAnnotation(createAnnotationInput: $createAnnotationInput) {
      ...annotation
    }
  }
`;

export class CreateAnnotationMutation extends Mutation {
  static conf = {
    GQL: CREATE_ANNOTATION_MUTATION,
    inputName: 'createAnnotationInput',
    resultPath: 'createAnnotation',
  };
}

const DELETE_ANNOTATION_MUTATION = gql`
  mutation DeleteAnnotation($deleteAnnotationInput: DeleteAnnotationInput) {
    deleteAnnotation(deleteAnnotationInput: $deleteAnnotationInput)
  }
`;

export class DeleteAnnotationMutation extends Mutation {
  static conf = {
    GQL: DELETE_ANNOTATION_MUTATION,
    inputName: 'deleteAnnotationInput',
    resultPath: 'deleteAnnotation',
  };
}

const PATCH_SUBSCRIPTION = gql`
  ${PATCH_FRAGMENT}
  subscription PatchSubscription($subscribePatchInput: SubscribePatchInput) {
    patch(subscribePatchInput: $subscribePatchInput) {
      ...patch
    }
  }
`;

export class PatchSubscription extends Subscription {
  static conf = {
    GQL: PATCH_SUBSCRIPTION,
    resultPath: 'patch',
  };
}

const PARTICIPANT_STATUS = gql`
  fragment participantStatus on ParticipantStatus {
    userId
    contextId
    resourceId
    activityStatus
  }
`;

const ACTIVE_PARTICIPANTS_QUERY = gql`
  ${PARTICIPANT_STATUS}
  query ActiveParticipantStatus(
    $getActiveParticipantStatus: GetActiveParticipantStatus!
  ) {
    activeParticipantStatus(
      getActiveParticipantStatus: $getActiveParticipantStatus
    ) {
      ...participantStatus
    }
  }
`;

export class ActiveParticipantStatusQuery extends Query {
  static conf = {
    GQL: ACTIVE_PARTICIPANTS_QUERY,
    resultPath: 'activeParticipantStatus',
  };
}

const PARTICIPANT_STATUS_SUBSCRIPTION = gql`
  ${PARTICIPANT_STATUS}
  subscription ParticipantStatusSubscription(
    $subscribeParticipantStatusInput: SubscribeParticipantStatusInput
  ) {
    participantStatus(
      subscribeParticipantStatusInput: $subscribeParticipantStatusInput
    ) {
      ...participantStatus
    }
  }
`;

export class ParticipantStatusSubscription extends Subscription {
  static conf = {
    GQL: PARTICIPANT_STATUS_SUBSCRIPTION,
    resultPath: 'participantStatus',
  };
}
